// LocalStorage utilities for practitioner note tracking

const DISMISS_KEY_PREFIX = 'prac_note_banner_dismissed_';
const VIEW_KEY_PREFIX = 'prac_note_viewed_';

/**
 * Store the timestamp when a user dismisses the practitioner note banner
 */
export const storePractitionerNoteDismissal = (kitId: string): void => {
  const timestamp = new Date().toISOString();
  localStorage.setItem(`${DISMISS_KEY_PREFIX}${kitId}`, timestamp);
};

/**
 * Store the timestamp when a user views the practitioner note
 */
export const storePractitionerNoteView = (kitId: string): void => {
  const timestamp = new Date().toISOString();
  localStorage.setItem(`${VIEW_KEY_PREFIX}${kitId}`, timestamp);
};

/**
 * Get the timestamp when the user last dismissed the banner for this kit
 */
export const getPractitionerNoteDismissalTime = (
  kitId: string,
): string | null => {
  return localStorage.getItem(`${DISMISS_KEY_PREFIX}${kitId}`);
};

/**
 * Get the timestamp when the user last viewed the note for this kit
 */
export const getPractitionerNoteViewTime = (kitId: string): string | null => {
  return localStorage.getItem(`${VIEW_KEY_PREFIX}${kitId}`);
};

/**
 * Normalize MySQL datetime format to ISO 8601 for consistent comparisons
 * MySQL format: "2026-03-06 20:26:58"
 * ISO format: "2026-03-06T20:26:58.000Z"
 */
const normalizeToISO = (dateString: string): string => {
  // If already in ISO format, return as-is
  if (dateString.includes('T')) {
    return dateString;
  }

  // Convert MySQL datetime to ISO format (assume UTC)
  return dateString.replace(' ', 'T') + '.000Z';
};

/**
 * Check if the banner should be shown based on dismissal/view timestamps vs published_at
 * Returns true if the banner should be shown
 */
export const shouldShowPractitionerNoteBanner = (
  kitId: string,
  publishedAt: string,
): boolean => {
  const dismissalTime = getPractitionerNoteDismissalTime(kitId);
  const viewTime = getPractitionerNoteViewTime(kitId);

  // Normalize published_at to ISO format for consistent comparison
  const normalizedPublishedAt = normalizeToISO(publishedAt);

  // If user dismissed the banner, check if it was for this version
  if (
    dismissalTime &&
    new Date(dismissalTime) >= new Date(normalizedPublishedAt)
  ) {
    return false; // User dismissed this version or a newer one
  }

  // If user viewed the note, check if it was for this version
  if (viewTime && new Date(viewTime) >= new Date(normalizedPublishedAt)) {
    return false; // User viewed this version or a newer one
  }

  // Show the banner - either no action taken or actions were for older version
  return true;
};
